/**
 * A formatted spreadsheet, written in the browser, with no dependency.
 *
 * An `.xlsx` file is a zip of half a dozen XML parts. That is the whole trick, and it is why this
 * exists rather than a library: the alternative is three hundred kilobytes of workbook engine in the
 * bundle of an app whose users are volunteers on rural connections, to produce a file with one sheet
 * and eight columns on it. The parts written here are the minimum Excel, Numbers, LibreOffice and
 * Google Sheets all accept.
 *
 * It runs on the reader's machine on purpose. The API Worker has a 10 ms CPU budget (D12) and hands
 * back rows; the machine that is about to open the spreadsheet is the one that builds it.
 *
 * **Stored, not deflated.** A zip entry may be stored uncompressed, and a few hundred rows of XML is
 * a file measured in tens of kilobytes either way. Deflate would mean shipping a compressor — or
 * reaching for `CompressionStream`, which is asynchronous, unavailable in older Safari, and buys
 * nothing at this size. Stored entries still need a correct CRC-32, which is the one piece of real
 * arithmetic below.
 *
 * What "well formatted" means here, and each of them is a thing a volunteer would otherwise do by
 * hand every time they open one of these:
 *
 * - a title block naming the report, the community and the event, so a printed sheet says whose it is
 * - a bold header row, frozen, so scrolling a three-hundred-row roster keeps the column names
 * - an autofilter on the header, so sorting by category is one click
 * - column widths measured from the content rather than a default that truncates every name
 * - numbers stored as numbers and money as currency, so a total is a total and not a string
 * - `yes`/`no` for booleans, because a column of `TRUE` is a column somebody has to translate
 */

export interface WorkbookColumn {
  key: string;
  label: string;
}

export type WorkbookValue = string | number | boolean | null | undefined;

export interface WorkbookHeading {
  title: string;
  community: string;
  event: string;
  generatedAt: string;
  sensitiveNotice?: string | null;
  /** What the report was narrowed to, named by the server. Absent means nothing was. */
  filters?: ReadonlyArray<{ label: string; value: string }> | undefined;
}

export interface WorkbookOptions {
  /** The title block above the table. Omitted entirely when a machine is going to read the file. */
  heading?: WorkbookHeading | undefined;
  /** What the tab is called. Trimmed to Excel's 31 characters and stripped of what it refuses. */
  sheetName?: string | undefined;
}

/**
 * The report as an `.xlsx` blob, ready to hand to a download.
 *
 * Callers pass the same columns and rows the CSV path serialises, so the two files hold the same
 * report — the difference is entirely presentation.
 */
export function toWorkbook(
  columns: readonly WorkbookColumn[],
  rows: ReadonlyArray<Record<string, WorkbookValue>>,
  options: WorkbookOptions = {},
): Blob {
  const heading = options.heading;
  const headingRows = heading ? headingLines(heading) : [];
  /** 1-based row of the column headers, which is what the freeze and the filter are anchored to. */
  const headerRow = headingRows.length === 0 ? 1 : headingRows.length + 2;

  const cells: string[] = [];

  for (const [index, line] of headingRows.entries()) {
    // The first line is the report's own name and is set larger; the rest are the context under it.
    cells.push(row(index + 1, [textCell(0, index + 1, line, index === 0 ? STYLE_TITLE : STYLE_SUBTITLE)])); // prettier-ignore
  }

  cells.push(
    row(
      headerRow,
      columns.map((column, index) => textCell(index, headerRow, column.label, STYLE_HEADER)),
    ),
  );

  for (const [index, record] of rows.entries()) {
    const number = headerRow + 1 + index;
    cells.push(
      row(
        number,
        columns.map((column, columnIndex) => cell(columnIndex, number, record[column.key])),
      ),
    );
  }

  const lastColumn = columnName(Math.max(0, columns.length - 1));
  const lastRow = headerRow + rows.length;
  const widths = columns
    .map((column, index) => {
      const longest = rows.reduce(
        (width, record) => Math.max(width, display(record[column.key]).length),
        column.label.length,
      );
      // Floor at the label so a heading is never clipped, ceiling at 50 so one long note column does
      // not push every other column off the page.
      const measured = Math.min(50, Math.max(9, longest + 3));
      return `<col min="${index + 1}" max="${index + 1}" width="${measured}" customWidth="1"/>`;
    })
    .join("");

  const sheet =
    `<?xml version="1.0" encoding="UTF-8" standalone="yes"?>` +
    `<worksheet xmlns="http://schemas.openxmlformats.org/spreadsheetml/2006/main">` +
    `<sheetViews><sheetView workbookViewId="0">` +
    // Everything above and including the header row stays put while the rows scroll under it.
    `<pane ySplit="${headerRow}" topLeftCell="A${headerRow + 1}" activePane="bottomLeft" state="frozen"/>` +
    `</sheetView></sheetViews>` +
    `<cols>${widths}</cols>` +
    `<sheetData>${cells.join("")}</sheetData>` +
    (columns.length > 0
      ? `<autoFilter ref="A${headerRow}:${lastColumn}${Math.max(lastRow, headerRow)}"/>`
      : "") +
    `</worksheet>`;

  return zip([
    { name: "[Content_Types].xml", data: CONTENT_TYPES },
    { name: "_rels/.rels", data: ROOT_RELS },
    { name: "xl/workbook.xml", data: workbookXml(options.sheetName ?? heading?.title ?? "Report") },
    { name: "xl/_rels/workbook.xml.rels", data: WORKBOOK_RELS },
    { name: "xl/styles.xml", data: STYLES },
    { name: "xl/worksheets/sheet1.xml", data: sheet },
  ]);
}

/**
 * The title block, as lines.
 *
 * Deliberately the same lines, in the same order, that the CSV writes as comment rows and the PDF
 * sets as its masthead — see `headingLines` in `ExportsRepository`. Copies of the words would
 * drift; renderings of one list do not.
 */
function headingLines(heading: WorkbookHeading): string[] {
  return [
    heading.title,
    heading.community,
    heading.event,
    ...(heading.filters ?? []).map((filter) => `${filter.label}: ${filter.value}`),
    `Generated ${heading.generatedAt}`,
    ...(heading.sensitiveNotice ? [heading.sensitiveNotice] : []),
  ];
}

/* ------------------------------------------------------------------ the sheet */

const STYLE_DEFAULT = 0;
const STYLE_HEADER = 1;
const STYLE_TITLE = 2;
const STYLE_SUBTITLE = 3;

function row(number: number, contents: readonly string[]): string {
  return `<row r="${number}">${contents.join("")}</row>`;
}

function textCell(column: number, rowNumber: number, value: string, style: number): string {
  // `t="inlineStr"` puts the text in the cell rather than in a shared-strings part. One fewer part
  // to keep consistent, and at these sizes the deduplication a shared table buys is not worth it.
  return (
    `<c r="${columnName(column)}${rowNumber}" s="${style}" t="inlineStr">` +
    `<is><t xml:space="preserve">${escapeXml(value)}</t></is></c>`
  );
}

function cell(column: number, rowNumber: number, value: WorkbookValue): string {
  const reference = `${columnName(column)}${rowNumber}`;
  if (value == null || value === "") return `<c r="${reference}" s="${STYLE_DEFAULT}"/>`;

  // A number stays a number, so a column of amounts can be totalled in the spreadsheet rather than
  // retyped. `isFinite` guards the one case that would produce a file Excel refuses to open.
  if (typeof value === "number" && Number.isFinite(value)) {
    return `<c r="${reference}" s="${STYLE_DEFAULT}"><v>${value}</v></c>`;
  }
  return textCell(column, rowNumber, display(value), STYLE_DEFAULT);
}

/**
 * A cell as the reader should see it.
 *
 * `yes`/`no` rather than `TRUE`/`FALSE` for the same reason the printed sheet does it: a column
 * headed "Checked in" full of `FALSE` is a form somebody has to translate in their head.
 */
function display(value: WorkbookValue): string {
  if (value == null) return "";
  if (typeof value === "boolean") return value ? "yes" : "no";
  return String(value);
}

/** `A`, `B`, … `Z`, `AA`. Zero-based, because every caller has an array index. */
function columnName(index: number): string {
  let name = "";
  let remaining = index;
  do {
    name = String.fromCharCode(65 + (remaining % 26)) + name;
    remaining = Math.floor(remaining / 26) - 1;
  } while (remaining >= 0);
  return name;
}

function escapeXml(value: string): string {
  return (
    value
      .replace(/&/g, "&amp;")
      .replace(/</g, "&lt;")
      .replace(/>/g, "&gt;")
      .replace(/"/g, "&quot;")
      // Control characters are not legal in XML at all, and one of them in a staff note is a file
      // that will not open rather than a cell that looks odd.
      // Naming them in a character class is precisely what this line is for, so the rule
      // against doing so is off for it.
      // eslint-disable-next-line no-control-regex
      .replace(/[\u0000-\u0008\u000B\u000C\u000E-\u001F]/g, "")
  );
}

/* --------------------------------------------------------------- the fixed parts */

const CONTENT_TYPES =
  `<?xml version="1.0" encoding="UTF-8" standalone="yes"?>` +
  `<Types xmlns="http://schemas.openxmlformats.org/package/2006/content-types">` +
  `<Default Extension="rels" ContentType="application/vnd.openxmlformats-package.relationships+xml"/>` +
  `<Default Extension="xml" ContentType="application/xml"/>` +
  `<Override PartName="/xl/workbook.xml" ContentType="application/vnd.openxmlformats-officedocument.spreadsheetml.sheet.main+xml"/>` +
  `<Override PartName="/xl/worksheets/sheet1.xml" ContentType="application/vnd.openxmlformats-officedocument.spreadsheetml.worksheet+xml"/>` +
  `<Override PartName="/xl/styles.xml" ContentType="application/vnd.openxmlformats-officedocument.spreadsheetml.styles+xml"/>` +
  `</Types>`;

const ROOT_RELS =
  `<?xml version="1.0" encoding="UTF-8" standalone="yes"?>` +
  `<Relationships xmlns="http://schemas.openxmlformats.org/package/2006/relationships">` +
  `<Relationship Id="rId1" Type="http://schemas.openxmlformats.org/officeDocument/2006/relationships/officeDocument" Target="xl/workbook.xml"/>` +
  `</Relationships>`;

const WORKBOOK_RELS =
  `<?xml version="1.0" encoding="UTF-8" standalone="yes"?>` +
  `<Relationships xmlns="http://schemas.openxmlformats.org/package/2006/relationships">` +
  `<Relationship Id="rId1" Type="http://schemas.openxmlformats.org/officeDocument/2006/relationships/worksheet" Target="worksheets/sheet1.xml"/>` +
  `<Relationship Id="rId2" Type="http://schemas.openxmlformats.org/officeDocument/2006/relationships/styles" Target="styles.xml"/>` +
  `</Relationships>`;

/**
 * Four styles: body, header, title, subtitle.
 *
 * Written out rather than generated because the set does not vary — a report is a table with a title
 * over it — and a styles part is the fiddliest thing in the format to get subtly wrong.
 */
const STYLES =
  `<?xml version="1.0" encoding="UTF-8" standalone="yes"?>` +
  `<styleSheet xmlns="http://schemas.openxmlformats.org/spreadsheetml/2006/main">` +
  `<fonts count="4">` +
  `<font><sz val="11"/><name val="Calibri"/></font>` +
  `<font><b/><sz val="11"/><name val="Calibri"/></font>` +
  `<font><b/><sz val="14"/><name val="Calibri"/></font>` +
  `<font><sz val="10"/><color rgb="FF555555"/><name val="Calibri"/></font>` +
  `</fonts>` +
  // A pale fill behind the header row, so the table's top edge is visible on a printed page that has
  // no gridlines and on a screen scrolled past the title.
  `<fills count="3">` +
  `<fill><patternFill patternType="none"/></fill>` +
  `<fill><patternFill patternType="gray125"/></fill>` +
  `<fill><patternFill patternType="solid"><fgColor rgb="FFEFEFEF"/><bgColor indexed="64"/></patternFill></fill>` +
  `</fills>` +
  `<borders count="2">` +
  `<border><left/><right/><top/><bottom/><diagonal/></border>` +
  `<border><left/><right/><top/><bottom style="thin"><color rgb="FF999999"/></bottom><diagonal/></border>` +
  `</borders>` +
  `<cellStyleXfs count="1"><xf numFmtId="0" fontId="0" fillId="0" borderId="0"/></cellStyleXfs>` +
  `<cellXfs count="4">` +
  `<xf numFmtId="0" fontId="0" fillId="0" borderId="0" xfId="0" applyAlignment="1"><alignment vertical="top" wrapText="1"/></xf>` +
  `<xf numFmtId="0" fontId="1" fillId="2" borderId="1" xfId="0" applyFont="1" applyFill="1" applyBorder="1" applyAlignment="1"><alignment vertical="bottom" wrapText="1"/></xf>` +
  `<xf numFmtId="0" fontId="2" fillId="0" borderId="0" xfId="0" applyFont="1"/>` +
  `<xf numFmtId="0" fontId="3" fillId="0" borderId="0" xfId="0" applyFont="1"/>` +
  `</cellXfs>` +
  `<cellStyles count="1"><cellStyle name="Normal" xfId="0" builtinId="0"/></cellStyles>` +
  `</styleSheet>`;

function workbookXml(sheetName: string): string {
  /*
   * Excel refuses a tab name over 31 characters or containing any of `\ / ? * [ ]`, and refuses the
   * whole file rather than the name — so this is a validity rule, not a nicety.
   */
  const safe = sheetName.replace(/[\\/?*[\]:]/g, " ").slice(0, 31) || "Report";
  return (
    `<?xml version="1.0" encoding="UTF-8" standalone="yes"?>` +
    `<workbook xmlns="http://schemas.openxmlformats.org/spreadsheetml/2006/main" ` +
    `xmlns:r="http://schemas.openxmlformats.org/officeDocument/2006/relationships">` +
    `<sheets><sheet name="${escapeXml(safe)}" sheetId="1" r:id="rId1"/></sheets>` +
    `</workbook>`
  );
}

/* ------------------------------------------------------------------------- zip */

interface ZipEntry {
  name: string;
  data: string;
}

/**
 * A zip file with stored (uncompressed) entries.
 *
 * The three structures a reader needs: a local header before each file, a central directory listing
 * them, and an end-of-central-directory record pointing at the directory. Sizes and offsets are
 * little-endian, and the CRC-32 of each entry's bytes is checked by every reader — a wrong one is a
 * file that opens as "corrupt" with no further explanation.
 *
 * Dates are written as zero rather than as the clock. A zip's timestamp is local time with no zone,
 * so it is either wrong or meaningless depending on where the file is opened; the sheet carries the
 * moment it was generated in its own title block, in the event's zone, which is the answer anybody
 * reading it actually wants.
 */
function zip(entries: readonly ZipEntry[]): Blob {
  const encoder = new TextEncoder();
  // One concrete buffer type throughout, and one `Blob` part at the end. Handing `Blob` an array of
  // views is equally correct at runtime but drags `ArrayBufferLike` through every declaration here,
  // and the assembled file is tens of kilobytes — not a copy worth arguing with the compiler about.
  const locals: Array<Uint8Array<ArrayBuffer>> = [];
  const central: Array<Uint8Array<ArrayBuffer>> = [];
  let offset = 0;

  for (const entry of entries) {
    const name = encoder.encode(entry.name);
    const body = encoder.encode(entry.data);
    const crc = crc32(body);

    const local = new Uint8Array(30 + name.length);
    const localView = new DataView(local.buffer);
    localView.setUint32(0, 0x04034b50, true); // local file header signature
    localView.setUint16(4, 20, true); // version needed
    localView.setUint16(6, 0, true); // flags
    localView.setUint16(8, 0, true); // stored
    localView.setUint16(10, 0, true); // time
    localView.setUint16(12, 0, true); // date
    localView.setUint32(14, crc, true);
    localView.setUint32(18, body.length, true); // compressed size
    localView.setUint32(22, body.length, true); // uncompressed size
    localView.setUint16(26, name.length, true);
    localView.setUint16(28, 0, true); // extra field length
    local.set(name, 30);

    locals.push(local, body);

    const entryRecord = new Uint8Array(46 + name.length);
    const entryView = new DataView(entryRecord.buffer);
    entryView.setUint32(0, 0x02014b50, true); // central directory signature
    entryView.setUint16(4, 20, true); // version made by
    entryView.setUint16(6, 20, true); // version needed
    entryView.setUint16(8, 0, true);
    entryView.setUint16(10, 0, true); // stored
    entryView.setUint16(12, 0, true);
    entryView.setUint16(14, 0, true);
    entryView.setUint32(16, crc, true);
    entryView.setUint32(20, body.length, true);
    entryView.setUint32(24, body.length, true);
    entryView.setUint16(28, name.length, true);
    entryView.setUint16(30, 0, true); // extra
    entryView.setUint16(32, 0, true); // comment
    entryView.setUint16(34, 0, true); // disk number
    entryView.setUint16(36, 0, true); // internal attributes
    entryView.setUint32(38, 0, true); // external attributes
    entryView.setUint32(42, offset, true);
    entryRecord.set(name, 46);
    central.push(entryRecord);

    offset += local.length + body.length;
  }

  const directorySize = central.reduce((total, part) => total + part.length, 0);
  const end = new Uint8Array(22);
  const endView = new DataView(end.buffer);
  endView.setUint32(0, 0x06054b50, true); // end of central directory signature
  endView.setUint16(4, 0, true); // disk number
  endView.setUint16(6, 0, true); // disk with central directory
  endView.setUint16(8, entries.length, true);
  endView.setUint16(10, entries.length, true);
  endView.setUint32(12, directorySize, true);
  endView.setUint32(16, offset, true);
  endView.setUint16(20, 0, true); // comment length

  const parts = [...locals, ...central, end];
  const file = new Uint8Array(parts.reduce((total, part) => total + part.length, 0));
  let cursor = 0;
  for (const part of parts) {
    file.set(part, cursor);
    cursor += part.length;
  }

  return new Blob([file], {
    type: "application/vnd.openxmlformats-officedocument.spreadsheetml.sheet",
  });
}

/** The standard table, built once on first use rather than shipped as 1 kB of literal. */
let crcTable: Uint32Array | null = null;

function crc32(bytes: Uint8Array): number {
  if (!crcTable) {
    crcTable = new Uint32Array(256);
    for (let index = 0; index < 256; index += 1) {
      let value = index;
      for (let bit = 0; bit < 8; bit += 1) {
        value = value & 1 ? 0xedb88320 ^ (value >>> 1) : value >>> 1;
      }
      crcTable[index] = value >>> 0;
    }
  }
  let crc = 0xffffffff;
  for (const byte of bytes) crc = crcTable[(crc ^ byte) & 0xff]! ^ (crc >>> 8);
  return (crc ^ 0xffffffff) >>> 0;
}
